/**
 * Meta (Facebook) Conversions API — server-side event relay.
 *
 * Forwards events to Meta's Conversions API so they are recorded even when
 * the browser pixel is blocked. Each event carries the same `event_id` as
 * the browser `fbq('track', …, { eventID })` call so Meta can deduplicate.
 *
 * Pixel id: `VITE_META_PIXEL_ID` (public, in wrangler.jsonc).
 * Access token: `META_CAPI_ACCESS_TOKEN` (secret, `wrangler secret put`).
 * Optional test code: `META_CAPI_TEST_EVENT_CODE` (Events Manager test tab).
 */

import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { env } from "cloudflare:workers";

const META_GRAPH_API_VERSION = "v21.0";
const ALLOWED_META_EVENT_SOURCE_HOSTS = new Set(["ai.zapify.pro", "zapify.pro"]);

export type MetaEventUserData = {
  email?: string;
  phone?: string;
  externalId?: string;
  fbp?: string;
  fbc?: string;
};

export type MetaEventCustomData = Record<string, unknown>;

export type SendMetaEventInput = {
  eventName: string;
  eventId: string;
  eventSourceUrl?: string;
  userData?: MetaEventUserData;
  customData?: MetaEventCustomData;
};

type MetaCapiUserData = {
  client_ip_address?: string;
  client_user_agent?: string;
  fbp?: string;
  fbc?: string;
  em?: string[];
  ph?: string[];
  external_id?: string[];
};

type MetaCapiEvent = {
  event_name: string;
  event_time: number;
  event_id: string;
  action_source: "website";
  event_source_url?: string;
  user_data: MetaCapiUserData;
  custom_data?: MetaEventCustomData;
};

async function sha256Hex(value: string): Promise<string> {
  const buf = new TextEncoder().encode(value);
  const digest = await crypto.subtle.digest("SHA-256", buf);
  const bytes = new Uint8Array(digest);
  let hex = "";
  for (let i = 0; i < bytes.length; i++) {
    hex += bytes[i].toString(16).padStart(2, "0");
  }
  return hex;
}

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

function normalizePhone(phone: string): string {
  return phone.replace(/\D+/g, "");
}

function isAllowedMetaEventSourceUrl(eventSourceUrl: string | undefined): boolean {
  if (!eventSourceUrl) return false;

  try {
    return ALLOWED_META_EVENT_SOURCE_HOSTS.has(new URL(eventSourceUrl).hostname);
  } catch {
    return false;
  }
}

export const sendMetaConversionEvent = createServerFn({ method: "POST" })
  .inputValidator((data: SendMetaEventInput) => {
    if (!data.eventName) throw new Error("eventName is required");
    if (!data.eventId) throw new Error("eventId is required");
    return data;
  })
  .handler(async ({ data }) => {
    const pixelId = env.VITE_META_PIXEL_ID;
    const accessToken = env.META_CAPI_ACCESS_TOKEN;
    const testEventCode = env.META_CAPI_TEST_EVENT_CODE;

    if (!pixelId || !accessToken) {
      return { success: false, skipped: true, reason: "missing_config" as const };
    }

    if (!isAllowedMetaEventSourceUrl(data.eventSourceUrl)) {
      return {
        success: false,
        skipped: true,
        reason: "unsupported_event_source_url" as const,
      };
    }

    const request = getRequest();
    const headers = request?.headers;
    const clientIp =
      headers?.get("cf-connecting-ip") ||
      headers?.get("x-forwarded-for")?.split(",")[0]?.trim() ||
      undefined;
    const userAgent = headers?.get("user-agent") || undefined;

    const user_data: MetaCapiUserData = {
      client_ip_address: clientIp,
      client_user_agent: userAgent,
      fbp: data.userData?.fbp,
      fbc: data.userData?.fbc,
    };

    if (data.userData?.email) {
      user_data.em = [await sha256Hex(normalizeEmail(data.userData.email))];
    }
    if (data.userData?.phone) {
      user_data.ph = [await sha256Hex(normalizePhone(data.userData.phone))];
    }
    if (data.userData?.externalId) {
      user_data.external_id = [await sha256Hex(data.userData.externalId.trim().toLowerCase())];
    }

    const event: MetaCapiEvent = {
      event_name: data.eventName,
      event_time: Math.floor(Date.now() / 1000),
      event_id: data.eventId,
      action_source: "website",
      event_source_url: data.eventSourceUrl,
      user_data,
      custom_data: data.customData,
    };

    const body: Record<string, unknown> = { data: [event] };
    if (testEventCode) body.test_event_code = testEventCode;

    try {
      const res = await fetch(
        `https://graph.facebook.com/${META_GRAPH_API_VERSION}/${pixelId}/events?access_token=${encodeURIComponent(accessToken)}`,
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(body),
        },
      );

      if (!res.ok) {
        const errorText = await res.text();
        console.error("Meta CAPI request failed", res.status, errorText);
        return { success: false, skipped: false, status: res.status } as const;
      }

      return { success: true, skipped: false } as const;
    } catch (error) {
      console.error("Meta CAPI request error", error);
      return { success: false, skipped: false, reason: "network_error" as const };
    }
  });
